import { Component, AfterViewInit, ViewChildren, QueryList, ElementRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { IconComponent, ButtonComponent } from '@kin/spark-ui';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-verify',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent],
  templateUrl: './verify.component.html',
  styleUrl: './verify.component.css',
})
export class VerifyComponent implements AfterViewInit {
  private router = inject(Router);
  private auth = inject(AuthService);

  email: string;
  digits: string[] = ['', '', '', '', '', ''];
  resendSent = false;
  invalidCode = false;
  verifying = false;
  errorMessage = '';

  @ViewChildren('digitInput') digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor() {
    this.email = history.state?.email ?? 'your email';
  }

  ngAfterViewInit() {
    this.digitInputs.first.nativeElement.focus();
  }

  onInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    this.digits[index] = value;
    input.value = value;

    if (value && index < 5) {
      this.digitInputs.toArray()[index + 1].nativeElement.focus();
    }

    if (this.isComplete) {
      setTimeout(() => this.verify(), 200);
    }
  }

  onKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      if (!this.digits[index] && index > 0) {
        this.digits[index - 1] = '';
        this.digitInputs.toArray()[index - 1].nativeElement.value = '';
        this.digitInputs.toArray()[index - 1].nativeElement.focus();
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.digitInputs.toArray()[index - 1].nativeElement.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      this.digitInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6) ?? '';
    this.digits = Array.from({ length: 6 }, (_, i) => pasted[i] ?? '');
    this.digitInputs.toArray().forEach((ref, i) => {
      ref.nativeElement.value = this.digits[i];
    });
    const nextEmpty = this.digits.findIndex(d => !d);
    const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
    setTimeout(() => this.digitInputs.toArray()[focusIndex].nativeElement.focus());
    if (this.isComplete) setTimeout(() => this.verify(), 300);
  }

  get isComplete(): boolean {
    return this.digits.every(d => d !== '');
  }

  verify() {
    if (this.verifying) return;

    const code = this.digits.join('');
    this.verifying = true;
    this.invalidCode = false;
    this.errorMessage = '';

    this.auth.verifyOtp(this.email, code).subscribe({
      next: (res) => {
        this.verifying = false;
        this.router.navigate([res.currentStep ? `/${res.currentStep}` : '/confirm']);
      },
      error: (err) => {
        this.verifying = false;
        this.invalidCode = true;
        this.errorMessage =
          err.status === 401
            ? 'Invalid or expired code. Please try again.'
            : 'Something went wrong. Please try again.';
        this.clearInputs();
      },
    });
  }

  resend() {
    this.auth.sendOtp(this.email).subscribe({
      next: () => {
        this.resendSent = true;
        setTimeout(() => (this.resendSent = false), 3000);
      },
      error: () => {
        this.errorMessage = 'Unable to resend code. Please try again.';
      },
    });
  }

  back() {
    this.router.navigate(['/login']);
  }

  private clearInputs() {
    this.digits = ['', '', '', '', '', ''];
    this.digitInputs.toArray().forEach(ref => (ref.nativeElement.value = ''));
    this.digitInputs.first.nativeElement.focus();
  }
}
